/* eslint-disable no-console */
import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {makeGetHealthSpecialtyEligibilityScript} from 'main/factories/usecases/private/booking-flow/remote-health-booking/get-health-specialty-eligibility-script-factory';
import {
	FeaturesEnum,
	QueryParamsForTaggingEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {EntryPointCodeEnum} from 'presentation/constants/entryPointCodeEnum';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';
import {TelemedicineEnum} from 'presentation/modules/booking/shared/constants/telemedicineEnum';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useDuplicityAuthStore} from 'presentation/modules/booking/shared/store/useDuplicityAuthStore';
import {useQueryParams} from 'presentation/pages';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {ParsedHealthAppointment} from 'presentation/pages/private/home/utils/parseHealthAppointments/types';
import {translations} from 'presentation/translations-new';
import {Specialty} from 'presentation/utils';
import {useState} from 'react';
import {useGetHealthDistrictsQuery} from '../../../../../HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {
	CheckBookingModal,
	CheckBookingModalEnum,
	GetHealthBookingBySpecialtyParams,
	HealthAppointmentWithSpecialtyNameSelected,
	UseHealthSpecialtiesSelectionProps,
} from './types';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';
import {useNavigate} from 'react-router-dom';
import {TELEMEDICINE_INFO} from 'presentation/modules/booking/shared/constants/telemedicineInfo';

const {loading} = translations['pt-br'].commons;
const {verifyingAuth} = translations['pt-br'].privateFlow.bookingFlow.specialtyFlow;

export function useHealthSpecialtiesSelection({
	handleNextStep,
}: UseHealthSpecialtiesSelectionProps) {
	const {
		SEARCH_PARAMS: {query},
		clearSearch,
	} = useQueryParams();
	const navigate = useNavigate();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const [checkBookingModal, setCheckBookingModal] = useState<CheckBookingModal>('none');
	const [scheduledBooking, setScheduledBooking] =
		useState<HealthAppointmentWithSpecialtyNameSelected | null>(null);
	const {verifyHealthAuthorization} = useBookingAuthorization();
	const {refetch: refetchAppointmentsList} = useGetHomeQuery();

	const {PROFESSIONAL_STEP} = getTelehealthStepPosition();

	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);

	const {ufCode, cityName, typeLocalName, specialtyCode, subSpecialtyCode} = bookingData;

	const districtParams: GetHealthDistrictsParams = {
		stateCode: ufCode?.toUpperCase(),
		locationName: cityName?.toUpperCase(),
		locationType: typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(specialtyCode),
	};

	const {refetch: refetchDistrictList} = useGetHealthDistrictsQuery({
		params: districtParams,
		enabled: false,
	});

	const loadingText =
		checkBookingModal === CheckBookingModalEnum.VERIFYING_AUTH ? verifyingAuth : loading;

	const openDuplicityDialog = (auth: boolean | undefined) => {
		useDuplicityAuthStore.getState().setDuplicityAuth(auth);
		setCheckBookingModal('duplicityState');
	};

	const handleSelectCard = (speciality: Specialty) => {
		return handleSelectCardHealthV2(speciality);
	};

	const getHealthBookingBySpecialty = ({
		specialtyCode,
		appointments,
	}: GetHealthBookingBySpecialtyParams) => {
		return appointments?.find(
			booking =>
				booking.specialtyCode === specialtyCode &&
				(booking.bookingType === BookingTypeEnum.HEALTH ||
					booking.bookingType === BookingTypeEnum.TELEHEALTH),
		) as ParsedHealthAppointment | undefined;
	};

	const handleManualLocationFlow = async (
		specialty: Specialty,
		shouldGoToScheduleScript: boolean,
	) => {
		setCheckBookingModal('verifyingDistricts');
		const hasTelehealth = specialty.telemedicine === TelemedicineEnum.IS_TELEMEDICINE;

		try {
			const {data: districtList} = await refetchDistrictList({throwOnError: true});
			setCheckBookingModal('none');

			if (hasTelehealth && districtList?.length === 0) {
				setActiveBookingFlowStep(value => value + 1);
			}

			clearSearch();
			handleNextStep();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('none');

			const {statusCode} = error as APIError;
			const areDistrictsEmpty = statusCode === HttpStatusCode.unprocessable;

			if (!areDistrictsEmpty && !shouldGoToScheduleScript) {
				setCheckBookingModal('error');
				return;
			}

			if (hasTelehealth) {
				setActiveBookingFlowStep(value => value + 1);
			}

			handleNextStep();
		}
	};

	const handleSelectCardHealthV2 = async (specialty: Specialty) => {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		const originalLocationData = {
			coordinates: bookingData.coordinates,
			ufCode: bookingData.ufCode,
			ufName: bookingData.ufName,
			cityName: bookingData.cityName,
			typeLocalName: bookingData.typeLocalName,
		};

		setBookingData(prev => ({
			...prev,
			specialtyCode: specialty.code,
			subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
			specialtyName: specialty.formattedText,
			hasTelehealth: specialty.telemedicine === TelemedicineEnum.IS_TELEMEDICINE,
			cityName: specialty?.cityName || prev.cityName || '',
			ufCode: specialty?.ufCode || prev.ufCode || '',
			shouldBackToSpecialtyStep: false,
		}));

		try {
			setCheckBookingModal('verifyingAuth');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = getHealthBookingBySpecialty({
				specialtyCode: specialty.code,
				appointments: updatedAppointmentsList?.appointments,
			});

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
					flExecutaSessao: alreadyScheduled.flExecutaSessao,
				});

				let isDuplicityAuthorized: boolean | undefined;
				if (alreadyScheduled.flExecutaSessao) {
					try {
						const {
							content: {specialtyAuthorization},
						} = await verifyHealthAuthorization({
							speciality: specialty.code,
							city: bookingData.cityName,
							uf: bookingData.ufCode,
						});
						isDuplicityAuthorized = !!specialtyAuthorization;
					} catch {
						isDuplicityAuthorized = false;
					}
				}
				openDuplicityDialog(isDuplicityAuthorized);
				return;
			}

			const isAuthorized = await handleVerifyAuthorization(specialty.code);
			if (!isAuthorized) return;

			if (
				isOnlyTelemedicineSpecialtyEnabled &&
				ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(specialty.code)
			) {
				setBookingData(prev => ({
					...prev,
					unitCode: TELEMEDICINE_INFO.code,
					unitName: TELEMEDICINE_INFO.name,
					unitAddress: '',
					ufCode: TELEMEDICINE_INFO.ufCode,
					ufName: TELEMEDICINE_INFO.ufName,
					cityName: TELEMEDICINE_INFO.cityName,
					coordinates: '',
					bookingRedirect: true,
					bookingType: BookingTypeEnum.TELEHEALTH,
					hourAt: '',
					dateAt: '',
					professionalCode: '',
					professionalName: '',
					typeLocalName: TypeLocalNameEnum.CIDADE,
					isManualLocation: true,
					initialBookingLocationData: {
						coordinates: originalLocationData.coordinates,
						ufCode: originalLocationData.ufCode,
						ufName: originalLocationData.ufName,
						cityName: originalLocationData.cityName,
						typeLocalName:
							originalLocationData.typeLocalName || TypeLocalNameEnum.CIDADE,
						bookingType: BookingTypeEnum.HEALTH,
					},
				}));
				setActiveBookingFlowStep(PROFESSIONAL_STEP);
				navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
				clearSearch();

				return;
			}

			let isEligibleForSchedulerScript = false;
			if (!bookingData?.isFavoriteDoctorsFlow) {
				const {
					content: {isEligible},
				} = await makeGetHealthSpecialtyEligibilityScript().get({
					specialtyCode: specialty.code,
					stateCode: bookingData.ufCode || specialty.ufCode || '',
					locationName: bookingData.cityName || specialty.cityName || '',
					locationType:
						bookingData.typeLocalName === TypeLocalNameEnum.COORDENADAS
							? TypeLocalNameEnum.CIDADE
							: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
					telemedicine: '',
					entryPointCode: EntryPointCodeEnum.V2,
				});
				isEligibleForSchedulerScript = isEligible;
			}

			setCheckBookingModal('none');

			if (isEligibleForSchedulerScript || alreadyScheduled) {
				await schedulerScript();
			} else {
				await schedulerDefault(specialty);
			}
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const schedulerScript = async () => {
		const currentSearchParams = new URLSearchParams(window.location.search);
		setBookingData(prev => ({
			...prev,
			isScheduleScript: true,
			bookingOrigin: BookingOriginEnum.BOOKING_SCRIPT,
		}));
		currentSearchParams.set(
			QueryParamsForTaggingEnum.BOOKING_ORIGIN,
			BookingOriginEnum.BOOKING_SCRIPT,
		);
	};

	const schedulerDefault = async (specialty: Specialty) => {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		try {
			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
				professionalCode: specialty.isFavoriteDoctorsFlow ? prev.professionalCode : '',
				professionalName: specialty.isFavoriteDoctorsFlow ? prev.professionalName : '',
				ufCode: prev.ufCode,
				ufName: prev.ufName,
				cityName: prev.cityName,
				unitCode: '',
				unitName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
			}));

			if (bookingData.isManualLocation) {
				await handleManualLocationFlow(specialty, false);
				return;
			}

			clearSearch();
			handleNextStep();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const handleVerifyAuthorization = async (specialtyCode: string) => {
		try {
			const {
				content: {specialtyAuthorization, suggestedSpecialties, smartScheduleSpecialty},
			} = await verifyHealthAuthorization({
				speciality: specialtyCode,
				city: bookingData.cityName,
				uf: bookingData.ufCode,
			});

			if (smartScheduleSpecialty && suggestedSpecialties && suggestedSpecialties.length > 0) {
				const [firstSuggestedSpecialty] = suggestedSpecialties;
				setBookingData(prev => ({
					...prev,
					specialtyCode: String(firstSuggestedSpecialty.code),
					specialtyName: String(firstSuggestedSpecialty.description),
				}));

				setCheckBookingModal('scriptState');
				return false;
			}

			if (!specialtyAuthorization) {
				setCheckBookingModal('needAuth');
				return false;
			}

			return true;
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			throw error;
		}
	};

	const resetBookingDataSpecialties = () => {
		setBookingData(prev => ({
			...prev,
			specialtyCode: '',
			subSpecialtyCode: undefined,
		}));
	};

	const handleChooseScriptSuggestion = async () => {
		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = getHealthBookingBySpecialty({
				specialtyCode: String(bookingData.specialtyCode),
				appointments: updatedAppointmentsList?.appointments,
			});
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				openDuplicityDialog(undefined);
				return;
			}
			handleNextStep();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	};

	const handleCheckHealthDuplicity = async (specialtyCode: string) => {
		try {
			setCheckBookingModal('verifyingDuplicity');
			const {data: updatedAppointmentsList} = await refetchAppointmentsList();
			const alreadyScheduled = getHealthBookingBySpecialty({
				specialtyCode,
				appointments: updatedAppointmentsList?.appointments,
			});
			setCheckBookingModal('none');
			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				openDuplicityDialog(undefined);
				return true;
			}
			return false;
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			return true;
		}
	};

	return {
		query,
		handleSelectCard,
		checkBookingModal,
		scheduledBooking,
		setCheckBookingModal,
		specialtyCode,
		subSpecialtyCode,
		loadingText,
		resetBookingDataSpecialties,
		handleChooseScriptSuggestion,
		handleCheckHealthDuplicity,
	};
}
